import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

// Layouts (always loaded — shell for all pages)
import { ProductLayout } from "./layouts/ProductLayout";
import { elementsConfig, forgeConfig } from "./layouts/productConfig";
import {
  ACCESS,
  ClientToAccess,
  ToAccess,
} from "./components/elements/accessRedirects";

/**
 * Retry a dynamic import once on chunk load failure (stale deploy).
 * If the import fails, reload the page to pick up the latest build.
 */
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  const key = "chunk_reload";
  return React.lazy(() =>
    factory()
      .then((m) => {
        // A successful chunk load proves we're on a live build — RE-ARM the
        // one-shot reload guard. Without this the key stayed set forever, so
        // the NEXT stale deploy in the same tab session blanked with no
        // recovery. (Re-arming only on success cannot loop: a persistently
        // failing chunk never clears the key.)
        try {
          sessionStorage.removeItem(key);
        } catch {
          /* sessionStorage unavailable — keep going */
        }
        return m;
      })
      .catch(() => {
        // Chunk failed — likely a stale deploy (cached index.html referencing
        // replaced asset hashes; the "opens twice with an error first" report,
        // 2026-07-27). Reload once to pick up the fresh build. The config now
        // also serves the app shell with Cache-Control: no-store (assets stay
        // immutable-cached), so this path should become rare.
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
        }
        // Return a blank component to avoid crash while reloading
        return { default: (() => null) as unknown as T };
      }),
  );
}

// Lazy-loaded page components — each product is a separate chunk
// Elements pages
const ProjectDetailPage = lazyRetry(() =>
  import("./components/elements/ProjectDetailPage").then((m) => ({
    default: m.ProjectDetailPage,
  })),
);
const PlatformsHubPage = lazyRetry(() =>
  import("./components/elements/PlatformFeaturesPage").then((m) => ({
    default: m.PlatformsHubPage,
  })),
);
const AiGovernancePage = lazyRetry(() =>
  import("./components/elements/AiGovernancePage").then((m) => ({
    default: m.AiGovernancePage,
  })),
);
const SmsRoutingPage = lazyRetry(() =>
  import("./components/elements/SmsRoutingPage").then((m) => ({
    default: m.SmsRoutingPage,
  })),
);
const UsersPage = lazyRetry(() =>
  import("./components/elements/UsersPage").then((m) => ({
    default: m.UsersPage,
  })),
);
const ActivityLogPage = lazyRetry(() =>
  import("./components/elements/ActivityLogPage").then((m) => ({
    default: m.ActivityLogPage,
  })),
);
const DatabaseConnectionsPage = lazyRetry(() =>
  import("./components/elements/DatabaseConnectionsPage").then((m) => ({
    default: m.DatabaseConnectionsPage,
  })),
);
const ApiConnectionsPage = lazyRetry(() =>
  import("./components/elements/ApiConnectionsPage").then((m) => ({
    default: m.ApiConnectionsPage,
  })),
);
const KeyManagementPage = lazyRetry(() =>
  import("./components/elements/KeyManagementPage").then((m) => ({
    default: m.KeyManagementPage,
  })),
);
const SecurityFindingsPage = lazyRetry(() =>
  import("./components/elements/SecurityFindingsPage").then((m) => ({
    default: m.SecurityFindingsPage,
  })),
);
const ServiceHealthPage = lazyRetry(() =>
  import("./components/elements/ServiceHealthPage").then((m) => ({
    default: m.ServiceHealthPage,
  })),
);
const FeedRegistryPage = lazyRetry(() =>
  import("./components/elements/FeedRegistryPage").then((m) => ({
    default: m.FeedRegistryPage,
  })),
);
const AccessBetaPage = lazyRetry(() =>
  import("./components/elements/AccessBetaPage").then((m) => ({
    default: m.AccessBetaPage,
  })),
);
const PermissionsPage = lazyRetry(() =>
  import("./components/elements/PermissionsPage").then((m) => ({
    default: m.PermissionsPage,
  })),
);
const PlatformStatusPage = lazyRetry(() =>
  import("./components/elements/PlatformStatusPage").then((m) => ({
    default: m.PlatformStatusPage,
  })),
);
const HealthPage = lazyRetry(() =>
  import("./components/elements/HealthPage").then((m) => ({
    default: m.HealthPage,
  })),
);
const NotificationsPage = lazyRetry(() =>
  import("./components/elements/NotificationsPage").then((m) => ({
    default: m.NotificationsPage,
  })),
);
const FailoverPage = lazyRetry(() =>
  import("./components/elements/FailoverPage").then((m) => ({
    default: m.FailoverPage,
  })),
);
const CommsTemplatesPage = lazyRetry(() =>
  import("./components/elements/CommsTemplatesPage").then((m) => ({
    default: m.CommsTemplatesPage,
  })),
);
const CommsTemplateDetailPage = lazyRetry(() =>
  import("./components/elements/CommsTemplateDetailPage").then((m) => ({
    default: m.CommsTemplateDetailPage,
  })),
);
const CommsCampaignsPage = lazyRetry(() =>
  import("./components/elements/CommsCampaignsPage").then((m) => ({
    default: m.CommsCampaignsPage,
  })),
);
const CommsCampaignWizard = lazyRetry(() =>
  import("./components/elements/CommsCampaignWizard").then((m) => ({
    default: m.CommsCampaignWizard,
  })),
);
const CommsCampaignDetailPage = lazyRetry(() =>
  import("./components/elements/CommsCampaignDetailPage").then((m) => ({
    default: m.CommsCampaignDetailPage,
  })),
);
const CommsContactsPage = lazyRetry(() =>
  import("./components/elements/CommsContactsPage").then((m) => ({
    default: m.CommsContactsPage,
  })),
);
const CommsContactDetailPage = lazyRetry(() =>
  import("./components/elements/CommsContactDetailPage").then((m) => ({
    default: m.CommsContactDetailPage,
  })),
);
const CommsSuppressionsPage = lazyRetry(() =>
  import("./components/elements/CommsSuppressionsPage").then((m) => ({
    default: m.CommsSuppressionsPage,
  })),
);
const CommsSavedSegmentsPage = lazyRetry(() =>
  import("./components/elements/CommsSavedSegmentsPage").then((m) => ({
    default: m.CommsSavedSegmentsPage,
  })),
);

// Forge pages
const DashboardPage = lazyRetry(() =>
  import("./components/forge/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  })),
);
const SourcesPage = lazyRetry(() =>
  import("./components/forge/SourcesPage").then((m) => ({
    default: m.SourcesPage,
  })),
);
const SourceWizard = lazyRetry(() =>
  import("./components/forge/SourceWizard").then((m) => ({
    default: m.SourceWizard,
  })),
);
const MappingPage = lazyRetry(() =>
  import("./components/forge/MappingPage").then((m) => ({
    default: m.MappingPage,
  })),
);
const TransformsPage = lazyRetry(() =>
  import("./components/forge/TransformsPage").then((m) => ({
    default: m.TransformsPage,
  })),
);
const MasterTablesPage = lazyRetry(() =>
  import("./components/forge/MasterTablesPage").then((m) => ({
    default: m.MasterTablesPage,
  })),
);
const ReferenceTablesPage = lazyRetry(() =>
  import("./components/forge/ReferenceTablesPage").then((m) => ({
    default: m.ReferenceTablesPage,
  })),
);
const JoinsPage = lazyRetry(() =>
  import("./components/forge/JoinsPage").then((m) => ({
    default: m.JoinsPage,
  })),
);
const VaultSyncPage = lazyRetry(() =>
  import("./components/forge/VaultSyncPage").then((m) => ({
    default: m.VaultSyncPage,
  })),
);
const RecipesPage = lazyRetry(() =>
  import("./components/forge/RecipesPage").then((m) => ({
    default: m.RecipesPage,
  })),
);
const AdminUploadPage = lazyRetry(() =>
  import("./components/forge/AdminUploadPage").then((m) => ({
    default: m.AdminUploadPage,
  })),
);
const UploadHistoryPage = lazyRetry(() =>
  import("./components/forge/UploadHistoryPage").then((m) => ({
    default: m.UploadHistoryPage,
  })),
);
const DataStatusPage = lazyRetry(() =>
  import("./components/forge/DataStatusPage").then((m) => ({
    default: m.DataStatusPage,
  })),
);
const IngestionHistoryPage = lazyRetry(() =>
  import("./components/forge/IngestionHistoryPage").then((m) => ({
    default: m.IngestionHistoryPage,
  })),
);
const CloudFeedsWizardPage = lazyRetry(() =>
  import("./components/forge/CloudFeedsWizardPage").then((m) => ({
    default: m.CloudFeedsWizardPage,
  })),
);
const QuarantinePage = lazyRetry(() =>
  import("./components/forge/QuarantinePage").then((m) => ({
    default: m.QuarantinePage,
  })),
);
const ReportTypesPage = lazyRetry(() =>
  import("./components/forge/ReportTypesPage").then((m) => ({
    default: m.ReportTypesPage,
  })),
);
const ReportTypeDetailPage = lazyRetry(() =>
  import("./components/forge/ReportTypeDetailPage").then((m) => ({
    default: m.ReportTypeDetailPage,
  })),
);
const ColumnManagementPage = lazyRetry(() =>
  import("./components/forge/ColumnManagementPage").then((m) => ({
    default: m.ColumnManagementPage,
  })),
);
const ModelsPage = lazyRetry(() =>
  import("./components/forge/ModelsPage").then((m) => ({
    default: m.ModelsPage,
  })),
);
const PendingReviewPage = lazyRetry(() =>
  import("./components/forge/PendingReviewPage").then((m) => ({
    default: m.PendingReviewPage,
  })),
);
const RecoveryPage = lazyRetry(() =>
  import("./components/forge/RecoveryPage").then((m) => ({
    default: m.RecoveryPage,
  })),
);
const RebuildHistoryPage = lazyRetry(() =>
  import("./components/forge/RebuildHistoryPage").then((m) => ({
    default: m.RebuildHistoryPage,
  })),
);
const SourceTemplatesPage = lazyRetry(() =>
  import("./components/forge/SourceTemplatesPage").then((m) => ({
    default: m.SourceTemplatesPage,
  })),
);
const QAReconcilePage = lazyRetry(
  () => import("./components/forge/QAReconcilePage"),
);

// Client upload portal
const UploadPage = lazyRetry(() =>
  import("./components/client/UploadPage").then((m) => ({
    default: m.UploadPage,
  })),
);
const UploadHistory = lazyRetry(() =>
  import("./components/client/UploadHistory").then((m) => ({
    default: m.UploadHistory,
  })),
);

function PageLoader() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "40vh",
      }}
    >
      <div style={{ color: "#6b7280", fontSize: "0.875rem" }}>Loading...</div>
    </div>
  );
}

function SubdomainRedirect() {
  const host = window.location.hostname;
  if (host.startsWith("elements.")) return <Navigate to="/elements" replace />;
  return <Navigate to="/forge" replace />;
}

export function App() {
  // Clear chunk reload flag on successful app load
  React.useEffect(() => {
    sessionStorage.removeItem("chunk_reload");
  }, []);
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Elements — Management Hub */}
        <Route
          path="/elements"
          element={<ProductLayout config={elementsConfig} />}
        >
          {/* Access is the start page; it replaced Clients & Access, the
              client page and Access (beta) (FORGE-411 phase 9, 2026-10-29) */}
          <Route index element={<Navigate to={ACCESS} replace />} />
          <Route path="access" element={<AccessBetaPage />} />
          <Route path="access-beta" element={<ToAccess />} />
          <Route path="setup" element={<ToAccess />} />
          <Route
            path="projects"
            element={<Navigate to={`${ACCESS}?view=projects`} replace />}
          />
          <Route path="projects/:id" element={<ProjectDetailPage />} />
          {/* Organization cleanup (2026-10-17): clients and client groups
              are set up in Setup; the old addresses go there */}
          <Route path="clients" element={<Navigate to={ACCESS} replace />} />
          <Route path="clients/:id" element={<ClientToAccess />} />
          {/* Packages retired (Organization cleanup, 2026-10-17): never
              switched on in Edge (EDGE_CAPABILITIES_ENABLED unset) */}
          <Route path="packages" element={<Navigate to={ACCESS} replace />} />
          <Route path="ai-governance" element={<AiGovernancePage />} />
          <Route
            path="access-review"
            element={<Navigate to="/elements/users?tab=checks" replace />}
          />
          {/* Platforms (FAC-084): which features exist on each platform's
              site; one page, a tab per site + Locks */}
          <Route path="platform-features" element={<PlatformsHubPage />} />
          <Route
            path="factor-settings"
            element={
              <Navigate to="/elements/platform-features?tab=factor" replace />
            }
          />
          <Route
            path="edge-settings"
            element={
              <Navigate to="/elements/platform-features?tab=edge" replace />
            }
          />
          <Route
            path="platform-locks"
            element={
              <Navigate to="/elements/platform-features?tab=locks" replace />
            }
          />
          <Route
            path="platforms"
            element={<Navigate to={`${ACCESS}?view=modules`} replace />}
          />
          <Route path="sms-routing" element={<SmsRoutingPage />} />
          <Route
            path="client-groups"
            element={<Navigate to={ACCESS} replace />}
          />
          <Route path="users" element={<UsersPage />} />
          <Route path="activity" element={<ActivityLogPage />} />
          <Route
            path="database-connections"
            element={<DatabaseConnectionsPage />}
          />
          <Route path="api-connections" element={<ApiConnectionsPage />} />
          <Route path="feed-registry" element={<FeedRegistryPage />} />
          <Route path="key-management" element={<KeyManagementPage />} />
          <Route path="security-findings" element={<SecurityFindingsPage />} />
          <Route path="service-health" element={<ServiceHealthPage />} />
          <Route path="admin-access" element={<PermissionsPage />} />
          <Route path="platform-status" element={<PlatformStatusPage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="failover" element={<FailoverPage />} />
          <Route path="comms/templates" element={<CommsTemplatesPage />} />
          <Route
            path="comms/templates/:id"
            element={<CommsTemplateDetailPage />}
          />
          <Route path="comms/campaigns" element={<CommsCampaignsPage />} />
          <Route path="comms/campaigns/new" element={<CommsCampaignWizard />} />
          <Route
            path="comms/campaigns/:id"
            element={<CommsCampaignDetailPage />}
          />
          <Route path="comms/contacts" element={<CommsContactsPage />} />
          <Route
            path="comms/contacts/:id"
            element={<CommsContactDetailPage />}
          />
          <Route
            path="comms/suppressions"
            element={<CommsSuppressionsPage />}
          />
          <Route
            path="comms/saved-segments"
            element={<CommsSavedSegmentsPage />}
          />
        </Route>

        {/* Forge — Data Pipeline */}
        <Route path="/forge" element={<ProductLayout config={forgeConfig} />}>
          <Route index element={<DashboardPage />} />
          <Route path="sources" element={<SourcesPage />} />
          <Route path="sources/new" element={<SourceWizard />} />
          <Route path="sources/:id" element={<SourceWizard />} />
          <Route path="source-templates" element={<SourceTemplatesPage />} />
          <Route path="mapping" element={<MappingPage />} />
          <Route path="transforms" element={<TransformsPage />} />
          <Route path="report-types" element={<ReportTypesPage />} />
          <Route path="report-types/:id" element={<ReportTypeDetailPage />} />
          <Route path="column-management" element={<ColumnManagementPage />} />
          <Route path="pending-review" element={<PendingReviewPage />} />
          <Route path="models" element={<ModelsPage />} />
          <Route path="master-tables" element={<MasterTablesPage />} />
          <Route
            path="reference-tables"
            element={<Navigate to="/forge/master-tables" replace />}
          />
          <Route path="table-joins" element={<JoinsPage />} />
          <Route path="vault-sync" element={<VaultSyncPage />} />
          <Route path="projection-recipes" element={<RecipesPage />} />
          <Route
            path="pipelines"
            element={<Navigate to="/forge/sources" replace />}
          />
          <Route
            path="pipelines/new"
            element={<Navigate to="/forge/sources/new" replace />}
          />
          <Route path="upload" element={<AdminUploadPage />} />
          <Route path="history" element={<UploadHistoryPage />} />
          <Route path="data-status" element={<DataStatusPage />} />
          <Route path="ingestion-history" element={<IngestionHistoryPage />} />
          <Route path="cloud-feeds/new" element={<CloudFeedsWizardPage />} />
          <Route path="quarantine" element={<QuarantinePage />} />
          <Route path="rebuild" element={<RecoveryPage />} />
          <Route path="rebuild-history" element={<RebuildHistoryPage />} />
          <Route path="qa" element={<QAReconcilePage />} />
        </Route>

        {/* Client upload portal (unchanged) */}
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/upload/history" element={<UploadHistory />} />

        {/* Backward compatibility: /admin/* → new routes */}
        <Route
          path="/admin/clients/onboard"
          element={<Navigate to={ACCESS} replace />}
        />
        <Route
          path="/admin/clients/:id"
          element={<Navigate to={ACCESS} replace />}
        />
        <Route
          path="/admin/clients"
          element={<Navigate to={ACCESS} replace />}
        />
        <Route
          path="/admin/users"
          element={<Navigate to="/elements/users" replace />}
        />
        <Route
          path="/admin/settings"
          element={<Navigate to="/elements/database-connections" replace />}
        />
        <Route
          path="/admin/activity"
          element={<Navigate to="/elements/activity" replace />}
        />
        <Route path="/admin/*" element={<Navigate to="/forge" replace />} />

        {/* Defaults — route based on subdomain */}
        <Route path="/" element={<SubdomainRedirect />} />
        <Route path="*" element={<SubdomainRedirect />} />
      </Routes>
    </Suspense>
  );
}
